import React, { memo, useContext, useMemo, useCallback, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useLocation } from 'react-router-dom';
import { Tooltip, Icon, Modal, Text, useTheme, Loader, Spacer } from '@Sage/hoi-poi-ui';
import {
    getCopilotStreamAnswer,
    CopilotContext,
    CopilotModal,
    COPILOT_CHAT_FEATURES,
    COPILOT_CHAT_INTERACTION_TYPES,
} from '@Sage/ssm-web5';
import classnames from 'classnames';
import { ACTIVITIES, CONVERSATIONS, MAILS } from 'constants/Entities';
import {
    ACTIVITY_TYPE_ANNOTATION,
    ACTIVITY_TYPE_CALL,
    ACTIVITY_TYPE_CHECKIN,
    ACTIVITY_TYPE_EMAIL,
    ACTIVITY_TYPE_OPPORTUNITY_CHECKIN,
    ACTIVITY_TYPE_FAST_CHECKIN,
    ACTIVITY_TYPE_VIDEO_CALL,
    ACTIVITY_TYPE_VIDEO_CHECKIN,
    ACTIVITY_TYPE_WHATSAPP,
} from 'models/ActivityModel';
import { ActivitiesService } from 'services';
import { getSignature } from 'services/MailingService';
import { EntityDetailActions, EntityActions, EntityModalActions } from 'actions';
import { getLiteral } from 'utils/getLiteral';
import { successToast, errorToast } from 'utils/toast';
import { getBackendBoolean, isBackendFalsy } from 'utils/fm';
import { ensureRoute } from 'utils/routes';
import { logEvent } from 'utils/tracking';
import { inConversations } from '../../utils/conversations';
import useFollow from '../../hooks/useFollow';
import MenuHoi from 'components/MenuHoi';
import useEntityDetail from 'containers/components/EntityDetail/hooks/useEntityDetail';
import useEmailEditor from 'containers/components/EmailEditor/hooks/useEmailEditor';
import metadata from 'lib/metadata';
import useCopilot from 'hooks/useCopilot';

import './styles.scss';

const EXECUTIVE_SUMMARY_PROMPT_ID = 14;
const CHRONOLOGICAL_SUMMARY_PROMPT_ID = 15;
const DRAFT_RESPONSE_PROMPT_ID = 37;

const mapStateToProps = (state, ownProps) => {
    return {
        user: state.config.userData,
        canDelete:
            ownProps?.activity?.Id &&
            !getBackendBoolean(ownProps?.activity?.IsReadOnly) &&
            state.config.permission?.crud_permission?.[ACTIVITIES.permission].delete,
        canEdit: state.config.permission.crud_permission[ACTIVITIES.permission].update,
        tabs: state.entityDetail.tabs || {},
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        followEntity: bindActionCreators(EntityActions, dispatch).followEntity,
        deleteEntity: bindActionCreators(EntityDetailActions, dispatch).deleteEntity,
        modalInit: bindActionCreators(EntityModalActions, dispatch).init,
        closeDetail: bindActionCreators(EntityDetailActions, dispatch).close,
    };
};

const ActionsOptions = memo(
    ({
        entity,
        activity,
        followEntity,
        deleteEntity,
        user,
        canDelete,
        canEdit,
        isFromWidgetDetail,
        isFromWidgetList,
        modalInit,
        onDeleteFromWidget,
        closeDetail,
        tabs,
    }) => {
        const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
        const [isDeleteLoading, setIsDeleteLoading] = useState(false);
        const [isDanaModalOpen, setIsDanaModalOpen] = useState(false);
        const [danaAnswerError, setDanaAnswerError] = useState(false);
        const [draftLoading, setDraftLoading] = useState(false);
        const [replyLoading, setReplyLoading] = useState(false);
        const [resendLoading, setResendLoading] = useState(false);
        const [chatProps, setChatProps] = useState({});

        const theme = useTheme();
        const location = useLocation();
        const { hasTabs, openCrud } = useEntityDetail();
        const { canUseEmail, getEmailDefaults, showEditor, mailContent } = useEmailEditor();

        const { clearChat } = useContext(CopilotContext);

        const chatId = useMemo(() => {
            return `${ACTIVITIES.entity}-${activity.Id}-${activity.ActivityType}`;
        }, [activity.ActivityType, activity.Id]);

        const { isUpdatingFollow, getHasFollow, handleFollow } = useFollow({
            id: activity?.Id,
            isFollowing: !isBackendFalsy(activity?.FollowingItem),
            followEntity,
        });

        const onConfirmDisclaimerSuccess = useCallback(
            ({ copilotModalAfterDisclaimer }) => {
                if (copilotModalAfterDisclaimer === EXECUTIVE_SUMMARY_PROMPT_ID) {
                    handleSetChatProps(CHRONOLOGICAL_SUMMARY_PROMPT_ID);
                    logEvent({
                        event: ACTIVITIES.trueName,
                        submodule: 'danaAssistant',
                        functionality: 'gptChronologicalSummary ',
                    });
                }
                if (copilotModalAfterDisclaimer === CHRONOLOGICAL_SUMMARY_PROMPT_ID) {
                    handleSetChatProps(CHRONOLOGICAL_SUMMARY_PROMPT_ID);
                    logEvent({
                        event: ACTIVITIES.trueName,
                        submodule: 'danaAssistant',
                        functionality: 'gptChronologicalSummary ',
                    });
                }
                if (copilotModalAfterDisclaimer === DRAFT_RESPONSE_PROMPT_ID) {
                    handleSetChatProps(DRAFT_RESPONSE_PROMPT_ID);
                }
            },
            [handleSetChatProps],
        );

        const {
            copilotActivated,
            copilotActivatedDisclaimer,
            copilotDisclaimerOnClose,
            renderCopilotDisclaimer,
            setCopilotModalAfterDisclaimer,
        } = useCopilot({
            onConfirmDisclaimerSuccess,
        });

        const onEditFromWidgetList = useCallback(() => {
            if (!activity || !entity) return;
            const { ActivityType, Id } = activity;

            let title = getLiteral('action_updatemanagement');

            const trackingPayload = {
                event: entity.trueName,
                submodule: 'activitiesWidget',
                functionality: 'edit',
            };

            switch (ActivityType) {
                case ACTIVITY_TYPE_CALL:
                    title = getLiteral('title_edit_call');
                    break;
                case ACTIVITY_TYPE_EMAIL:
                    title = getLiteral('title_edit_email');
                    break;
                default:
                    break;
            }

            logEvent(trackingPayload);

            modalInit({
                entity: ACTIVITIES,
                id: Id,
                labels: {
                    title,
                    success: getLiteral('succes_entityupdatedsuccessfully'),
                    error: getLiteral('label_failed_update_contact_salesforce'),
                    deleteModalTitle: getLiteral('title_delete_activities'),
                    successDelete: getLiteral('succes_entitydeletedsuccessfully'),
                },
                hideDelete: [ACTIVITY_TYPE_CALL, ACTIVITY_TYPE_EMAIL].includes(ActivityType),
                extraInfo: {
                    activityType: ActivityType,
                },
            });
        }, [activity, modalInit, entity]);

        const onEditFromDetail = useCallback(() => {
            if (!activity) return;
            if (hasTabs) {
                closeDetail({ tabs, isFromTab: true });
                openCrud();
            } else {
                ensureRoute(`${ACTIVITIES.route}/${activity.Id}/${activity.ActivityType}/edit`);
            }
        }, [activity, closeDetail, hasTabs, openCrud, tabs]);

        const onEdit = useCallback(
            (e) => {
                if (isFromWidgetList) {
                    e.stopPropagation();
                    onEditFromWidgetList();
                    return;
                }
                onEditFromDetail();
            },
            [isFromWidgetList, onEditFromWidgetList, onEditFromDetail],
        );

        const onError = useCallback(() => {
            setDanaAnswerError(true);
        }, []);

        const getChatProps = useCallback(
            (promptId) => {
                return {
                    overrides: { disableAgentSwitcher: true, disableChatInput: true },
                    session: {
                        features: [COPILOT_CHAT_FEATURES.PROMPT_ENTITY],
                    },
                    interactions: [
                        {
                            item: {
                                content: {
                                    promptId: promptId.toString(), // Because BE
                                    entity: MAILS.trueName,
                                    entityId: parseInt(activity.Id, 10), // Because BE
                                },
                                type: COPILOT_CHAT_INTERACTION_TYPES.PARAMS,
                            },
                        },
                    ],
                    cb: { onError },
                };
            },
            [activity.Id, onError],
        );

        const handleSetChatProps = useCallback(
            (promptId) => {
                clearChat({ chatId });
                setIsDanaModalOpen(true);
                setChatProps(getChatProps(promptId));
            },
            [chatId, clearChat, getChatProps],
        );

        const danAIIconMenu = useMemo(() => {
            return <div className="activity-modal-dana-menu-icon">{metadata.SageCopilotIcon}</div>;
        }, []);

        const onCloseDana = useCallback(() => {
            setDanaAnswerError('');
            setIsDanaModalOpen(false);
            copilotDisclaimerOnClose();
        }, [copilotDisclaimerOnClose]);

        const onDraft = useCallback(
            ({ fromReply = false }) => {
                if (fromReply) setReplyLoading(true);
                else setDraftLoading(true);
                Promise.all([
                    getSignature(),
                    getCopilotStreamAnswer(getChatProps(DRAFT_RESPONSE_PROMPT_ID)),
                    ActivitiesService.getEmailWithImages(activity.Id),
                ])
                    .then(([resSignature, data, emailWithImages]) => {
                        const signature = resSignature?.data?.body;
                        let replyDefaults = getEmailDefaults(
                            activity,
                            'RE',
                            emailWithImages?.bodyFull,
                        );
                        replyDefaults.content =
                            data?.replace(/(?:\r\n|\r|\n)/g, '<br>') +
                            '<br>' +
                            signature +
                            replyDefaults.content;
                        replyDefaults.overwriteContent = true;
                        showEditor(replyDefaults, {
                            event: ACTIVITIES.trueName,
                            submodule: 'sendEmail',
                            functionality: 'reply',
                        });
                    })
                    .catch((err) => {
                        errorToast({ text: getLiteral('label_dana_disclaimer_error') });
                        console.error(err);
                    })
                    .finally(() => {
                        if (fromReply) setReplyLoading(false);
                        else setDraftLoading(false);
                    });
            },
            [activity, getChatProps, getEmailDefaults, showEditor],
        );

        const actions = useMemo(() => {
            if (!activity?.ActivityType) return [];
            const items = [];
            const activityType = parseInt(activity.ActivityType, 10);
            const followingItem = !isBackendFalsy(activity.FollowingItem);

            const withCopilot = activityType === ACTIVITY_TYPE_EMAIL;

            if (withCopilot && copilotActivated && !isFromWidgetList && activity?.Body?.length) {
                let actions = [
                    {
                        title: <Text>{getLiteral('action_chatgpt_summary')}</Text>,
                        description: (
                            <Text type="caption">
                                {getLiteral('label_subtitle_chatgpt_summary')}
                            </Text>
                        ),
                        icon: 'textInfo',
                        onClick: () => {
                            if (!copilotActivatedDisclaimer) {
                                setCopilotModalAfterDisclaimer(EXECUTIVE_SUMMARY_PROMPT_ID);
                            } else {
                                handleSetChatProps(EXECUTIVE_SUMMARY_PROMPT_ID);
                                logEvent({
                                    event: ACTIVITIES.trueName,
                                    submodule: 'danaAssistant',
                                    functionality: 'gptExecutiveSummary ',
                                });
                            }
                        },
                        className: 'activity-detail__menu-options__dana-summary',
                    },
                    {
                        title: <Text>{getLiteral('action_chatgpt_chronological_summary')}</Text>,
                        description: (
                            <Text type="caption">
                                {getLiteral('label_subtitle_chatgpt_chronological_summary')}
                            </Text>
                        ),
                        icon: 'valueList',
                        onClick: () => {
                            if (!copilotActivatedDisclaimer) {
                                setCopilotModalAfterDisclaimer(CHRONOLOGICAL_SUMMARY_PROMPT_ID);
                            } else {
                                handleSetChatProps(CHRONOLOGICAL_SUMMARY_PROMPT_ID);
                                logEvent({
                                    event: ACTIVITIES.trueName,
                                    submodule: 'danaAssistant',
                                    functionality: 'gptChronologicalSummary ',
                                });
                            }
                        },
                        className: 'activity-detail__menu-options__chatgpt-chronological-summary',
                    },
                ];

                items.push({
                    title: getLiteral('helptext_chatgpt_summary'),
                    icon: (
                        <div className="detail-action">
                            {draftLoading && (
                                <Loader
                                    size="tiny"
                                    className="activity-detail__menu-options__draft-response__loader"
                                />
                            )}
                            {!draftLoading && <MenuHoi items={actions}>{danAIIconMenu}</MenuHoi>}
                        </div>
                    ),
                    noTooltip: draftLoading,
                    isMenu: true,
                    className: 'activity-detail-menu__chatGPT',
                });
            }

            if (isFromWidgetDetail) return items;

            if (getHasFollow(activityType) && !isFromWidgetList) {
                items.push({
                    title: getLiteral(followingItem ? 'action_unfollow' : 'action_follow'),
                    icon: followingItem ? (
                        <Icon name="flag" size="medium" color={theme.colors.actionMajor[500]} />
                    ) : (
                        <Icon
                            name="flagOutline"
                            size="medium"
                            color={theme.colors.actionMinor[500]}
                        />
                    ),
                    onClick: () => {
                        !isUpdatingFollow && handleFollow();
                    },
                    className: 'activity-detail-menu__follow',
                });
            }

            const isActivityEditable =
                activityType === ACTIVITY_TYPE_ANNOTATION ||
                activityType === ACTIVITY_TYPE_CHECKIN ||
                activityType === ACTIVITY_TYPE_OPPORTUNITY_CHECKIN ||
                activityType === ACTIVITY_TYPE_FAST_CHECKIN ||
                activityType === ACTIVITY_TYPE_VIDEO_CALL ||
                activityType === ACTIVITY_TYPE_VIDEO_CHECKIN ||
                activityType === ACTIVITY_TYPE_WHATSAPP ||
                activityType === ACTIVITY_TYPE_EMAIL ||
                activityType === ACTIVITY_TYPE_CALL;

            const isActivityDeletable =
                activityType === ACTIVITY_TYPE_ANNOTATION ||
                activityType === ACTIVITY_TYPE_CHECKIN ||
                activityType === ACTIVITY_TYPE_OPPORTUNITY_CHECKIN ||
                activityType === ACTIVITY_TYPE_FAST_CHECKIN ||
                activityType === ACTIVITY_TYPE_VIDEO_CALL ||
                activityType === ACTIVITY_TYPE_VIDEO_CHECKIN ||
                activityType === ACTIVITY_TYPE_WHATSAPP;

            let isEditable =
                canEdit && isActivityEditable && !getBackendBoolean(activity.IsReadOnly);

            let isDeletable =
                canDelete && isActivityDeletable && !getBackendBoolean(activity.IsReadOnly);

            if (canUseEmail && activityType === ACTIVITY_TYPE_EMAIL) {
                function reply() {
                    setReplyLoading(true);
                    ActivitiesService.getEmailWithImages(activity.Id)
                        .then((emailWithImages) => {
                            let replyDefaults = getEmailDefaults(
                                activity,
                                !activity.Subject?.startsWith('RE:') ? 'RE' : undefined,
                                emailWithImages?.bodyFull,
                            );
                            if (activity?.Opportunity) {
                                replyDefaults.opportunity = {
                                    id: activity?.Opportunity.Id,
                                    reference: activity?.Opportunity.Name,
                                };
                            }
                            showEditor(replyDefaults, {
                                event: ACTIVITIES.trueName,
                                submodule: 'sendEmail',
                                functionality: 'reply',
                            });
                        })
                        .catch((e) => {
                            console.error(e);
                            errorToast({ text: getLiteral('error_tryitagain') });
                        })
                        .finally(() => setReplyLoading(false));
                }
                const isMenu = withCopilot;
                const replyDanaItems = [
                    {
                        title: <Text>{getLiteral('action_reply')}</Text>,
                        description: (
                            <Text type="caption">{getLiteral('label_response_email_desc')}</Text>
                        ),
                        icon: 'email',
                        onClick: reply,
                        className: 'activity-detail__menu-options__draft-response',
                    },
                    {
                        title: <Text>{getLiteral('action_chatgpt_email_draft_response')}</Text>,
                        description: (
                            <Text type="caption">
                                {getLiteral('label_subtitle_chatgpt_email_draft_response')}
                            </Text>
                        ),
                        customIcon: (
                            <>
                                <Spacer x={1} />
                                {danAIIconMenu}
                            </>
                        ),
                        onClick: () => {
                            if (!copilotActivatedDisclaimer) {
                                setCopilotModalAfterDisclaimer(DRAFT_RESPONSE_PROMPT_ID);
                            } else {
                                if (canUseEmail) onDraft({ fromReply: true });
                                else {
                                    handleSetChatProps(DRAFT_RESPONSE_PROMPT_ID);
                                }
                            }
                        },
                        className: 'activity-detail__menu-options__draft-response',
                    },
                ];
                items.push({
                    title: getLiteral('action_reply'),
                    icon: replyLoading ? (
                        <Loader
                            size="tiny"
                            className="activity-detail__menu-options__tiny__loader"
                        />
                    ) : isMenu ? (
                        <div className="detail-action">
                            <MenuHoi items={replyDanaItems}>
                                {<Icon name="reply" size="medium" />}
                            </MenuHoi>
                        </div>
                    ) : (
                        <Icon name="reply" size="medium" />
                    ),

                    onClick: !isMenu ? reply : undefined,
                    className: 'activity-detail-menu__reply',
                    noTooltip: replyLoading,
                    isMenu,
                });

                items.push({
                    title: getLiteral('action_resend'),
                    icon: resendLoading ? (
                        <Loader
                            size="tiny"
                            className="activity-detail__menu-options__tiny__loader"
                        />
                    ) : (
                        <Icon name="resend" size="medium" />
                    ),
                    onClick: () => {
                        setResendLoading(true);
                        ActivitiesService.getEmailWithImages(activity.Id)
                            .then((emailWithImages) => {
                                let resendDefaults = getEmailDefaults(
                                    activity,
                                    !activity.Subject?.startsWith('FW:') ? 'FW' : undefined,
                                    emailWithImages?.bodyFull,
                                );
                                if (activity?.Opportunity) {
                                    resendDefaults.opportunity = {
                                        id: activity?.Opportunity.Id,
                                        reference: activity?.Opportunity.Name,
                                    };
                                }
                                showEditor(
                                    { ...resendDefaults, to: [] },
                                    {
                                        event: ACTIVITIES.trueName,
                                        submodule: 'sendEmail',
                                        functionality: 'resend',
                                    },
                                );
                            })
                            .catch((e) => {
                                console.error(e);
                                errorToast({ text: getLiteral('error_tryitagain') });
                            })
                            .finally(() => setResendLoading(false));
                    },
                    className: 'activity-detail-menu__resend',
                    noTooltip: resendLoading,
                });
            }

            if (isEditable) {
                items.push({
                    title: getLiteral('action_edit'),
                    icon: <Icon name="edit" size="medium" />,
                    onClick: onEdit,
                    className: 'activity-detail-menu__edit',
                });
            }

            if (isDeletable) {
                items.push({
                    title: getLiteral('action_delete'),
                    icon: <Icon name="deleteRaw" size="medium" />,
                    onClick: (e) => {
                        e.stopPropagation();
                        setIsDeleteModalOpen(true);
                    },
                    className: 'activity-detail-menu__delete',
                });
            }

            return items;
        }, [
            activity,
            copilotActivated,
            isFromWidgetList,
            isFromWidgetDetail,
            getHasFollow,
            canEdit,
            canDelete,
            canUseEmail,
            draftLoading,
            danAIIconMenu,
            copilotActivatedDisclaimer,
            setCopilotModalAfterDisclaimer,
            handleSetChatProps,
            theme.colors.actionMajor,
            theme.colors.actionMinor,
            isUpdatingFollow,
            handleFollow,
            replyLoading,
            resendLoading,
            getEmailDefaults,
            showEditor,
            onDraft,
            onEdit,
        ]);

        const onCancelDelete = useCallback(() => {
            setIsDeleteModalOpen(false);
        }, []);

        const onConfirmDelete = useCallback(() => {
            if (onDeleteFromWidget) {
                setIsDeleteLoading(true);
                onDeleteFromWidget(activity)
                    .then(() => {
                        successToast({
                            text: getLiteral('succes_entitydeletedsuccessfully'),
                        });
                        setIsDeleteLoading(false);
                        setIsDeleteModalOpen(false);
                    })
                    .catch(() => {
                        setIsDeleteLoading(false);
                        console.error('Error deleting');
                    });

                return;
            }
            const activeEntity = inConversations(location) ? CONVERSATIONS : null;
            setIsDeleteLoading(true);
            deleteEntity(ACTIVITIES, activeEntity, true)
                .then(() => {
                    logEvent({
                        event: ACTIVITIES.trueName,
                        functionality: 'delete',
                    });
                    successToast({
                        text: getLiteral('succes_entitydeletedsuccessfully'),
                    });
                    setIsDeleteLoading(false);
                    setIsDeleteModalOpen(false);
                })
                .catch(() => {
                    setIsDeleteLoading(false);
                    console.error('error deleting entity from detail');
                });
        }, [deleteEntity, location, onDeleteFromWidget, activity]);

        const modalDeleteContentOverride = useMemo(
            () => ({
                confirmButton: { type: 'primary-error' },
            }),
            [],
        );

        const modalTitle = useMemo(
            () => (
                <Text
                    color={theme.colors.utility.textSecondary}
                    className="copilot-chat-modal__assistantFor"
                    isTruncated
                >{`${getLiteral('label_assistant_for')} ${getLiteral('label_email')}`}</Text>
            ),
            [theme.colors.utility.textSecondary],
        );

        const chatModalProps = useMemo(
            () => ({
                chatSetup: {
                    chatId,
                    locale: user.locale,
                    handleEmail: canUseEmail ? mailContent : undefined,
                    isError: danaAnswerError,
                    ...chatProps,
                },
                metadata,
                open: isDanaModalOpen,
                onClose: onCloseDana,
                title: modalTitle,
            }),
            [
                canUseEmail,
                chatId,
                chatProps,
                danaAnswerError,
                isDanaModalOpen,
                mailContent,
                modalTitle,
                onCloseDana,
                user.locale,
            ],
        );

        return (
            <div key={activity.Id} className="activities-actions-options">
                {actions?.length > 0 &&
                    actions.reduce((arr, current, index) => {
                        let className = 'detail-action';
                        if (current.className) {
                            className = classnames('detail-action', current.className);
                        }

                        let icon;
                        if (current?.isMenu) {
                            icon = (
                                <div className={className} key={index}>
                                    {current.icon}
                                </div>
                            );
                            if (current.title && !current.noTooltip) {
                                arr.push(
                                    <Tooltip content={current.title} key={index}>
                                        {current.icon}
                                    </Tooltip>,
                                );
                            } else {
                                arr.push(icon);
                            }
                            return arr;
                        }

                        if (!current.icon || !current.onClick) return arr;
                        icon = (
                            <div className={className} onClick={current.onClick} key={index}>
                                {current.icon}
                            </div>
                        );
                        if (current.title) {
                            arr.push(
                                <Tooltip content={current.title} key={index}>
                                    {icon}
                                </Tooltip>,
                            );
                        } else {
                            arr.push(icon);
                        }
                        return arr;
                    }, [])}
                {canDelete && (
                    <Modal
                        title={getLiteral('action_delete_activity')}
                        className="fm-entity-modal__container"
                        overlayClassName="fm-entity-modal__delete-overlay"
                        isOpen={isDeleteModalOpen}
                        onRequestClose={onCancelDelete}
                        onCancel={onCancelDelete}
                        onConfirm={onConfirmDelete}
                        isConfirmLoading={isDeleteLoading}
                        isConfirmDisabled={isDeleteLoading}
                        confirmText={getLiteral('helptext_confirm')}
                        cancelText={getLiteral('action_cancel')}
                        size="small"
                        overrides={modalDeleteContentOverride}
                    >
                        <Text>{getLiteral('confirm_delete_activity')}</Text>
                    </Modal>
                )}
                {renderCopilotDisclaimer}
                <CopilotModal {...chatModalProps} />
            </div>
        );
    },
);

export default connect(mapStateToProps, mapDispatchToProps)(ActionsOptions);
